import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, AlertTriangle, CheckCircle2, Globe2, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import type {
  ClientStatusResponse,
  StatusComponent,
  StatusCountryItem,
  StatusNetwork,
  StatusSectionNetwork,
  StatusUptimeDay,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { SectionLabel } from '@/components/ui/calm';
import {
  Skeleton,
  SkeletonCard,
  SkeletonLine,
  SkeletonPage,
  SkeletonStats,
} from '@/components/ui/skeleton';
import { getWebApp } from '@/lib/telegram';
import { countryName, regionName } from '@/lib/geo';
import { NetworkPulse } from '@/components/status/NetworkPulse';
import { Flag } from '@/components/ui/flag';

export const Route = createFileRoute('/status')({ component: StatusPage });

/** Бэк отдаёт 90 дней истории; на телефоне рисуем последние 30 — 90 полосок дают
 *  по 2px и читаются как сплошная полоса. Процент аптайма при этом остаётся за 90. */
const VISIBLE_DAYS = 30;

type StatusKey = 'operational' | 'degraded' | 'down' | 'no_data';

const DOT: Record<StatusKey, string> = {
  operational: 'bg-[#38e0a5]',
  degraded: 'bg-[#f0b04e]',
  down: 'bg-[#f0656a]',
  no_data: 'bg-white/25',
};

const TEXT: Record<StatusKey, string> = {
  operational: 'text-[#38e0a5]',
  degraded: 'text-[#f0b04e]',
  down: 'text-[#f0656a]',
  no_data: 'text-text-muted',
};

const ICON: Record<StatusKey, typeof CheckCircle2> = {
  operational: CheckCircle2,
  degraded: AlertTriangle,
  down: XCircle,
  no_data: Activity,
};

const TITLE_KEY: Record<StatusKey, string> = {
  operational: 'Status operational',
  degraded: 'Status degraded',
  down: 'Status down',
  no_data: 'Status no data',
};

type Section = 'term' | 'lifetime';

function asKey(s: string | undefined): StatusKey {
  return s === 'operational' || s === 'degraded' || s === 'down' ? s : 'no_data';
}

function StatusPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  const { data, isPending } = useQuery({
    queryKey: ['client-network-status'],
    queryFn: () => api.networkStatus(),
    refetchInterval: 60_000,
    // Мини-апп свёрнут — не тратим мобильный трафик и батарею юзера.
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });

  const relTime = (iso: string | null): string => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(diff) || diff < 60_000) return t('Status time now');
    const min = Math.floor(diff / 60_000);
    if (min < 60) return t('Status time min', { count: min });
    const h = Math.floor(min / 60);
    if (h < 24) return t('Status time hour', { count: h });
    return t('Status time day', { count: Math.floor(h / 24) });
  };

  if (isPending) return <StatusSkeleton />;
  if (!data) {
    return (
      <div className="p-4 pb-24">
        <div className="rounded-2xl border border-white/8 bg-card p-8 text-center text-sm text-text-muted">
          {t('Status unavailable')}
        </div>
      </div>
    );
  }

  return <StatusView data={data} relTime={relTime} />;
}

function StatusView({
  data,
  relTime,
}: {
  data: ClientStatusResponse;
  relTime: (iso: string | null) => string;
}) {
  const { t } = useTranslation();
  const key = asKey(data.overall);
  const Icon = ICON[key];
  const term: StatusSectionNetwork = { component: data.component, network: data.network };
  const lifetime = data.lifetime ?? null;
  // Раздел клиента идёт первым, и hero про его сеть: `overall` бэк считает по ней же.
  const primary: Section = lifetime && data.primary_section === 'lifetime' ? 'lifetime' : 'term';
  const heroNet = primary === 'lifetime' && lifetime ? lifetime : term;
  const sections: [Section, StatusSectionNetwork][] | null = lifetime
    ? primary === 'lifetime'
      ? [['lifetime', lifetime], ['term', term]]
      : [['term', term], ['lifetime', lifetime]]
    : null;
  const sectionTitle = (s: Section) => (s === 'lifetime' ? t('Section lifetime') : t('Section term'));
  const uptime = heroNet.component.uptime_pct_90d;

  return (
    <div className="p-4 pb-24 space-y-5">
      {/* Hero: пульс сети + текущее состояние и две честные цифры */}
      <div className="relative overflow-hidden rounded-[28px] border border-white/8 bg-card">
        <NetworkPulse className="absolute inset-0 h-full w-full opacity-60" />
        <div className="relative z-10 p-5">
          {/* Сетей на экране две, а заголовок про одну: без подписи «Все системы
              работают» спорило бы с красной карточкой второй сети ниже. */}
          {sections && (
            <SectionLabel className="mb-2 text-text-muted">
              {t('Status section network', { section: sectionTitle(primary) })}
            </SectionLabel>
          )}
          <div className="flex items-center gap-2.5">
            <span className="relative flex size-2.5">
              <span className={cn('absolute inline-flex size-full animate-ping rounded-full opacity-60', DOT[key])} />
              <span className={cn('relative inline-flex size-2.5 rounded-full', DOT[key])} />
            </span>
            <Icon className={cn('size-4', TEXT[key])} />
            <h1 className="text-lg font-bold">{t(TITLE_KEY[key])}</h1>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2.5">
            <div className="rounded-2xl border border-white/8 bg-white/[0.04] p-3.5">
              <div className="font-mono-label text-text-muted">{t('Status locations')}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">{heroNet.network.countries}</div>
            </div>
            <div className="rounded-2xl border border-white/8 bg-white/[0.04] p-3.5">
              <div className="font-mono-label text-text-muted">{t('Status uptime')}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums text-[#38e0a5]">
                {uptime != null ? `${uptime.toFixed(2)}%` : '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {sections ? (
        // Две сети: у каждого раздела свой блок, своя история и свои страны.
        sections.map(([id, net]) => (
          <section key={id} className="space-y-2.5">
            <SectionLabel className="px-1">{sectionTitle(id)}</SectionLabel>
            <div className="space-y-3">
              <NetworkCard component={net.component} />
              <CountryRegions network={net.network} />
            </div>
          </section>
        ))
      ) : (
        <>
          {/* Единственный компонент, который что-то значит для клиента */}
          <section className="space-y-2.5">
            <SectionLabel className="px-1">{t('Status vpn network')}</SectionLabel>
            <NetworkCard component={data.component} />
          </section>

          <section className="space-y-2.5">
            <SectionLabel className="px-1">{t('Status countries')}</SectionLabel>
            <CountryRegions network={data.network} />
          </section>
        </>
      )}

      {data.updated_at && (
        <p className="text-center text-[11px] text-text-faint">
          {t('Status checked', { time: relTime(data.updated_at) })}
        </p>
      )}
    </div>
  );
}

/** Карточка VPN-сети: текущее состояние и полоса аптайма по дням. */
function NetworkCard({ component }: { component: StatusComponent }) {
  const { t } = useTranslation();
  const key = asKey(component.status);
  // Раньше сбой и отказ тоже подписывались «Работает», только другим цветом. С двумя
  // сетями на экране для второй сети эта подпись единственная, и врать ей нельзя.
  const label = {
    operational: t('Status label ok'),
    degraded: t('Status degraded'),
    down: t('Status down'),
    no_data: t('Status label none'),
  }[key];
  return (
    <div className="rounded-2xl border border-white/8 bg-card p-4">
      <div className="mb-2.5 flex items-center gap-2.5">
        <span className={cn('size-2.5 shrink-0 rounded-full', DOT[key])} />
        <span className="text-sm font-medium">{t('Status vpn network')}</span>
        <span className={cn('ml-auto text-xs font-semibold', TEXT[key])}>{label}</span>
      </div>
      <UptimeBar days={component.days} />
      <div className="mt-1.5 flex justify-between text-[10px] text-text-faint">
        <span>{t('Status days ago', { count: VISIBLE_DAYS })}</span>
        <span>{t('Status today')}</span>
      </div>
    </div>
  );
}

/** Страны сети по регионам. Без имён серверов и без миллисекунд: клиенту важно
 *  «работает ли страна», а не как называется узел и сколько до него от нашего сервера. */
function CountryRegions({ network }: { network: StatusNetwork }) {
  const { t } = useTranslation();
  const regions = Object.entries(network.regions);
  if (regions.length === 0) {
    return (
      <div className="rounded-2xl border border-white/8 bg-card p-6 text-center text-sm text-text-muted">
        {t('Status empty')}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {regions.map(([region, countries]) => (
        <div key={region} className="rounded-2xl border border-white/8 bg-card overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-3.5 pb-2">
            <Globe2 className="size-3.5 text-accent" />
            <span className="text-[13px] font-semibold">{regionName(region, t)}</span>
            <span className="ml-auto text-[11px] text-text-faint tabular-nums">{countries.length}</span>
          </div>
          <div className="px-2 pb-2">
            {countries.map((c) => (
              <CountryRow key={c.flag + c.country_ru} country={c} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function UptimeBar({ days }: { days: StatusUptimeDay[] }) {
  return (
    <div className="flex h-6 items-stretch gap-[2px]">
      {days.slice(-VISIBLE_DAYS).map((d) => (
        <div
          key={d.day}
          className={cn(
            'h-full min-w-[2px] flex-1 rounded-[2px]',
            d.status === 'no_data' ? 'bg-white/[0.08]' : DOT[asKey(d.status)],
          )}
        />
      ))}
    </div>
  );
}

function CountryRow({ country }: { country: StatusCountryItem }) {
  const { i18n } = useTranslation();
  // up===0 && total===0 — узлы ещё не пинговали: серая точка, а не красная,
  // иначе свежая синхронизация выглядела бы как тотальный отказ сети.
  const dot =
    country.total === 0
      ? 'bg-white/25'
      : country.up === country.total
        ? 'bg-[#38e0a5]'
        : country.up === 0
          ? 'bg-[#f0656a]'
          : 'bg-[#f0b04e]';

  return (
    <div className="flex min-h-11 items-center gap-2.5 rounded-xl px-2">
      <span className={cn('size-2 shrink-0 rounded-full', dot)} />
      <span className="text-lg leading-none">
        <Flag flag={country.flag} code={country.country_code} />
      </span>
      <span className="text-[13px] font-medium">{countryName(country.country_code, country.country_ru, i18n.language)}</span>
      {country.total > 0 && (
        <span className="ml-auto text-[11px] tabular-nums text-text-faint">
          {country.up}/{country.total}
        </span>
      )}
    </div>
  );
}

/** Hero с состоянием сети и двумя цифрами, карточка VPN-сети, список регионов. */
function StatusSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonPage className="space-y-5">
      <SkeletonCard className="rounded-[28px] min-h-0 p-5 space-y-4">
        <div className="flex items-center gap-2.5">
          <Skeleton className="size-2.5 rounded-full" />
          <Skeleton className="size-4 rounded" />
          <SkeletonLine size="title" className="w-40" />
        </div>
        <SkeletonStats count={2} cols={2} className="gap-2.5" itemClassName="rounded-2xl" />
      </SkeletonCard>

      <section className="space-y-2.5">
        <SectionLabel className="px-1">{t('Status vpn network')}</SectionLabel>
        <SkeletonCard className="rounded-2xl min-h-0 p-4 space-y-2.5">
          <div className="flex items-center gap-2.5">
            <Skeleton className="size-2.5 rounded-full shrink-0" />
            <SkeletonLine className="w-2/5" />
          </div>
          <SkeletonLine size="sm" className="w-3/5" />
        </SkeletonCard>
      </section>

      <SkeletonCard className="h-40 rounded-2xl" />
    </SkeletonPage>
  );
}
